import React from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { useMediaQuery } from 'react-responsive';
import postal from 'postal';
import classNames from 'classnames';
import {
  propTypes, defaultProps, isContactable,
} from './prop-types';
import Avatar from '../shared/avatar';
import Author from './elements/author';
import Actions from './elements/actions';
import Blurb from './elements/blurb';
import ContactForm from './mp-components/contact-form';
import Expertise from './mp-components/expertise';
import ErrorBoundary from '../../shared/error-boundary';
import theme from '../../../assets/stylesheets/_settings.global.scss';

const Profile = styled.section`
  font-size: 18px;
  position: relative;
  ${props => props.theme.isExpanded && `
    border-bottom: 1px dashed ${props.theme.primaryColor};
    margin-top: 34px;
    padding-bottom: 10px;
  `};

  &.--form-expanded {
    border-radius: 0 0 4px 4px;
    box-shadow: 0 10px 20px rgba(53, 65, 76, 0.2);
    transition: box-shadow .3s ease-in;

    > .micro-profile-areas-of-expertise {
      border-radius: 0;
      border-bottom: 0;
    }
  }
`;

const Contributor = styled.section`
  border: ${props => ((!props.theme.isNotMobile || props.theme.isExpanded || props.theme.isOnWireSide)
    ? 'none'
    : `1px solid ${props.theme.secondaryColorLight}`
  )};
  padding: ${props => ((!props.theme.isNotMobile || props.theme.isExpanded || props.theme.isOnWireSide)
    ? '0'
    : '16px'
  )};
  ${props => props.theme.isNotMobile && `
    border-radius: 4px 4px 0 0;
  `};
`;

const Header = styled.header`
  display: flex;
  flex-direction: ${props => (props.theme.isOnWireSide ? 'column' : 'row')};
  ${props => props.theme.isOnWireSide && `
    margin: 0;
  `}

  > a {
    flex-shrink: 0;
  }
`;

export default function MicroProfile(props) {
  const [formExpanded, setFormExpanded] = React.useState(false);
  const isNotMobile = useMediaQuery({ query: `(min-width: ${theme.breakpointLargeMin})` });
  const isSmallMobile = useMediaQuery({ query: `(max-width: ${theme.breakpointSmallMax})` });
  const isNotXLarge = useMediaQuery({ query: `(max-width: ${theme.breakpointLargeMax})` });
  const {
    profile, clickDataAttributes, isExpanded, isOnWireSide,
  } = props;
  const contactable = isContactable(props);

  const state = {
    ...props,
    ...theme,
    contactable,
    isNotMobile,
    isSmallMobile,
    isLarge: isNotMobile,
    isNotXLarge,
  };

  React.useEffect(() => {
    postal.subscribe({
      channel: 'micro-profile.commands',
      topic: 'present-lead-capture-form',
      callback: () => !isExpanded && !isOnWireSide && setFormExpanded(true),
    });
  }, [isExpanded, isOnWireSide]);

  return (
    <ErrorBoundary>
      <ThemeProvider theme={state}>
        <Profile
          className={classNames('micro-profile', { '--form-expanded': formExpanded })}
          data-profile-id={profile.id}
          data-profile-path={`contributors/${profile.slug}`}
          data-target={!isExpanded && 'wire-micro-profile'}
          data-target-view={isOnWireSide && 'side'}
        >
          <Contributor>
            <Header>
              <Avatar {...{ ...profile, clickDataAttributes }} />
              <Author {...props} />
            </Header>
            <Blurb />
          </Contributor>
          <Expertise {...props} />
          {!!(!isOnWireSide && contactable) && <ContactForm {...props} />}
          {!isNotMobile && <Actions />}
        </Profile>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

MicroProfile.propTypes = propTypes;
MicroProfile.defaultProps = defaultProps;
